import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { BdPageable, Paginator } from '@bindoc-projects/platform-lib/data';
import {
  AccessGraph,
  AccessGraphOptions,
  BdCompany,
  BdCompanyGroup,
  BdSharePermission,
  BdSharePermissionHelper,
  MIN_MODIFIED_DATE,
  ShareableFolderObj,
  ShareableLabel,
  ShareableObj,
  ShareableReference,
  ShareableType,
  ShareTarget,
  ShareTargetType,
} from '@bindoc-projects/resource-domain-lib';
import {
  catchError,
  EMPTY,
  firstValueFrom,
  forkJoin,
  Observable,
  of,
} from 'rxjs';
import { expand, map, reduce, switchMap } from 'rxjs/operators';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';
import { BdShareablesStore } from '../../store/resources/resources.store';
import { BdGraphApiUser, BdMyUsersParams } from '../../users/user.model';
import { SharePreview } from './share-preview.model';

@Injectable({
  providedIn: 'root',
})
export class BdShareablesService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _environmentService = inject(BdAppEnvironmentService);
  private readonly _store = new BdShareablesStore(
    this._environmentService.load(ApiEndpointKey.BD_PERMISSION_RESOURCE_URL),
  );
  private _cache: { [key: string]: Promise<boolean> } = {};

  public myPermission(
    shareableType: ShareableType,
    resourceId: string,
  ): Observable<{
    shareTarget: ShareTarget;
    shareableReference: ShareableReference;
    allowedShareablePermissions: BdSharePermission[];
    highestPermission: BdSharePermission;
  }> {
    const url = this._store.checkMyPermissionOn(shareableType, resourceId);

    return this._httpClient
      .get<{
        shareTarget: ShareTarget;
        shareableReference: ShareableReference;
        allowedShareablePermissions: BdSharePermission[];
      }>(url)
      .pipe(
        map((response) => ({
          ...response,
          highestPermission: BdSharePermissionHelper.getHighestPermission(
            response.allowedShareablePermissions,
          ),
        })),
      );
  }

  public hasPermission(
    shareableType: ShareableType,
    resourceId: string,
    permission: BdSharePermission,
  ): Promise<boolean> {
    const cacheKey = this._getKey(resourceId, shareableType, permission);
    let cachedReq = this._cache[cacheKey];

    if (!cachedReq) {
      const url = this._store.checkMyPermissionOn(shareableType, resourceId);

      cachedReq = firstValueFrom(
        this._httpClient
          .get<{
            shareTarget: ShareTarget;
            shareableReference: ShareableReference;
            allowedShareablePermissions: BdSharePermission[];
          }>(url, {
            params: new HttpParams().set('minimumPermission', permission),
          })
          .pipe(
            map((response) =>
              response.allowedShareablePermissions.some(
                (allowedPermission) => allowedPermission === permission,
              ),
            ),
            catchError(() => of(false)),
          ),
      );

      this._cache[cacheKey] = cachedReq;
    }

    return cachedReq;
  }

  public archive(
    shareableType: ShareableType,
    shareableId: string,
  ): Observable<void> {
    const url = this._store.archive(shareableType, shareableId);

    return this._httpClient.delete<void>(url);
  }

  /**
   * Fetch all shareables of the current user (renamed from `sharedResources`).
   *
   * Behavior
   *  - If neither type filter is provided, all shareable types are returned.
   *  - `shareableType` whitelists; `excludeShareableType` blacklists. Both can be combined.
   *  - `companyId` (optional) scopes to a specific company; omit for the current default scope.
   *  - When `page.query` is set, it is forwarded as the `query` parameter.
   */
  public myShareables(
    page?: Paginator,
    shareableType?: ShareableType[],
    excludeShareableType?: ShareableType[],
    companyId?: string,
    labels?: string[],
  ): Observable<BdPageable<ShareableObj>> {
    const url = this._store.myShareables();

    let params = new HttpParams();
    if (shareableType && shareableType.length > 0) {
      params = params.appendAll({ shareableType: shareableType });
    }

    if (excludeShareableType && excludeShareableType.length > 0) {
      params = params.appendAll({ excludeShareableType: excludeShareableType });
    }

    if (companyId) {
      params = params.set('companyId', companyId);
    }

    if (labels && labels.length > 0) {
      params = params.appendAll({ label: labels });
    }

    if (page) {
      params = params.set('size', page.pageSize).set('page', page.pageIndex);

      if (page.query) {
        params = params.set('query', page.query);
      }

      if (page.sort && page.sort.length > 0) {
        params = params.appendAll({ sort: page.sort });
      }
    }

    return this._httpClient.get<BdPageable<ShareableObj>>(url, { params });
  }

  /**
   * Convenience wrapper around `myShareables` that returns the entry matching
   * `(id, type)` from the first page of results, or `undefined` if missing.
   */
  public sharedResourceById(
    id: string,
    type?: ShareableType,
  ): Observable<ShareableObj | undefined> {
    const types = type ? [type] : undefined;
    return this.myShareables(
      Paginator.of({ pageSize: 10, pageIndex: 0, query: id }),
      types,
    ).pipe(map((response) => response.content.find((s) => s.id === id)));
  }

  public rootFolder(): Observable<ShareableFolderObj> {
    return this._httpClient.get<ShareableFolderObj>(this._store.rootFolder());
  }

  public folder(folderId: string): Observable<ShareableFolderObj> {
    return this._httpClient
      .get<ShareableFolderObj>(this._store.folder(folderId))
      .pipe(
        map((folder) => ({
          ...folder,
          children: (folder.children ?? []).map((child) => ({
            ...child,
            // The API may omit `modifiedDate`, but sorting and the date
            // template require a parseable value — fall back to the earliest
            // representable date so such children sink to the bottom.
            modifiedDate: child.modifiedDate || MIN_MODIFIED_DATE,
          })),
        })),
      );
  }

  public createFolder(
    name: string,
    parentFolderId: string,
  ): Observable<ShareableFolderObj> {
    return this._httpClient.post<ShareableFolderObj>(this._store.createFolder(), {
      name,
      parentFolderId,
    });
  }

  /**
   * Renames a folder. The backend exposes this as `PUT /folders/{folderId}/`
   * with a `{ name }` body and returns the updated folder.
   *
   * Requires UPDATE permission on the target folder.
   */
  public renameFolder(
    folderId: string,
    name: string,
  ): Observable<ShareableFolderObj> {
    return this._httpClient.put<ShareableFolderObj>(
      this._store.renameFolder(folderId),
      { name },
    );
  }

  public moveShareable(
    id: string,
    type: ShareableType,
    targetFolderId: string,
  ): Observable<void> {
    return this._httpClient.post<void>(this._store.moveShareable(), {
      shareableReference: { id, type },
      targetFolderId,
    });
  }

  public restoreShareable(
    type: ShareableType,
    id: string,
  ): Observable<void> {
    return this._httpClient.put<void>(this._store.restoreShareable(type, id), {});
  }

  public recycleBin(): Observable<ShareableFolderObj> {
    return this._httpClient.get<ShareableFolderObj>(this._store.recycleBin());
  }

  public groupRootFolder(groupId: string): Observable<ShareableFolderObj> {
    return this._httpClient.get<ShareableFolderObj>(
      this._store.groupRootFolder(groupId),
    );
  }

  public groupRecycleBin(groupId: string): Observable<ShareableFolderObj> {
    return this._httpClient.get<ShareableFolderObj>(
      this._store.groupRecycleBin(groupId),
    );
  }

  public myGroups(): Observable<BdCompanyGroup[]> {
    return this._httpClient.get<BdCompanyGroup[]>(this._store.myGroups());
  }

  /**
   * Fetches a single group by id (`GET /groups/{id}`) → `{ id, name,
   * companyId }`. Used to resolve a deep-linked group's display name without
   * loading the whole company/group list.
   */
  public group(groupId: string): Observable<BdCompanyGroup> {
    return this._httpClient.get<BdCompanyGroup>(this._store.group(groupId));
  }

  /**
   * Fetches the current user's companies across **all** pages.
   *
   * The backend serves `/companies/` as a Spring `Page` (default size 20), so
   * a single request only returns the first page. This walks every page via
   * `expand` and concatenates their `content`, returning the complete list —
   * the global company selector needs all companies, not just the first page.
   */
  public myCompanies(): Observable<BdCompany[]> {
    const pageSize = 100;

    return this._fetchCompaniesPage(0, pageSize).pipe(
      expand((page) =>
        this._hasNextCompaniesPage(page)
          ? this._fetchCompaniesPage(page.number + 1, pageSize)
          : EMPTY,
      ),
      reduce<BdPageable<BdCompany>, BdCompany[]>(
        (all, page) => all.concat(page.content),
        [],
      ),
    );
  }

  /**
   * Fetches a **single** page of the current user's companies, sorted by name.
   *
   * Backs the lazy/virtual-scroll company selector: `GET /companies/` accepts
   * `page`, `size`, `sort` (allowed field: `name`) and an optional server-side
   * `query`, and returns a Spring `Page`. Only the requested slice is loaded.
   */
  public searchMyCompanies(
    page: number,
    size: number,
    query?: string,
  ): Observable<BdPageable<BdCompany>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size)
      .append('sort', 'name,asc');

    const trimmedQuery = query?.trim();
    if (trimmedQuery) {
      params = params.set('query', trimmedQuery);
    }

    return this._httpClient.get<BdPageable<BdCompany>>(
      this._store.myCompanies(),
      { params },
    );
  }

  /**
   * Fetches a single company by id (`GET /companies/{id}`) → `{ id, name }`.
   * Used to reveal a deep-linked group whose company is not in the sidebar's
   * loaded page, so a company node can be built without the full list.
   */
  public company(companyId: string): Observable<BdCompany> {
    return this._httpClient.get<BdCompany>(this._store.company(companyId));
  }

  /**
   * Fetches a **single** page of a company's groups, sorted by name.
   *
   * Backs the lazy move-form tree: `GET /companies/{id}/groups/` accepts
   * `page`, `size`, `sort` (allowed field: `name`) and an optional server-side
   * `query`, and returns a Spring `Page` — a company's groups are loaded only
   * when its node is expanded, instead of eagerly for every company.
   */
  public groupsOfCompany(
    companyId: string,
    page: number,
    size: number,
    query?: string,
  ): Observable<BdPageable<BdCompanyGroup>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size)
      .append('sort', 'name,asc');

    const trimmedQuery = query?.trim();
    if (trimmedQuery) {
      params = params.set('query', trimmedQuery);
    }

    return this._httpClient.get<BdPageable<BdCompanyGroup>>(
      this._store.companyGroups(companyId),
      { params },
    );
  }

  /**
   * Resolves which company group a folder lives in, or `null` for the user's
   * own scope.
   *
   * The API exposes no folder→group field, but a folder response carries its
   * ancestor chain up to the scope ROOT (`ShareableFolderObj.parent`). We take
   * that root and match it against the user root, then against the roots of my
   * groups. Resolves to `null` when the folder sits in the user root or nothing
   * matches — callers then treat it as the user's own scope. `companyId`, when
   * provided, narrows the groups probed (fewer root lookups); when omitted, all
   * of my groups are considered.
   */
  public resolveFolderGroup(
    folderId: string,
    companyId?: string,
  ): Observable<BdCompanyGroup | null> {
    return this.folder(folderId).pipe(
      switchMap((folder) => {
        const rootId = this._rootFolderIdOf(folder);
        return this.rootFolder().pipe(
          switchMap((userRoot) =>
            userRoot.folderId === rootId
              ? of(null)
              : this._matchGroupByRoot(rootId, companyId),
          ),
        );
      }),
      catchError(() => of(null)),
    );
  }

  private _matchGroupByRoot(
    rootId: string,
    companyId?: string,
  ): Observable<BdCompanyGroup | null> {
    return this.myGroups().pipe(
      switchMap((groups) => {
        const candidates = companyId
          ? groups.filter((g) => g.companyId === companyId)
          : groups;
        if (candidates.length === 0) {
          return of(null);
        }
        return forkJoin(
          candidates.map((group) =>
            this.groupRootFolder(group.id).pipe(
              map((root) => (root.folderId === rootId ? group : null)),
              catchError(() => of(null)),
            ),
          ),
        ).pipe(map((matches) => matches.find((g) => g !== null) ?? null));
      }),
    );
  }

  /** Walks a folder's ancestor chain to the scope ROOT and returns its id. */
  private _rootFolderIdOf(folder: ShareableFolderObj): string {
    let ancestor = folder.parent;
    if (!ancestor) {
      // No ancestor info, or the folder is itself the root.
      return folder.folderId;
    }
    while (ancestor.parent) {
      ancestor = ancestor.parent;
    }
    return ancestor.folderId;
  }

  private _fetchCompaniesPage(
    page: number,
    size: number,
  ): Observable<BdPageable<BdCompany>> {
    return this.searchMyCompanies(page, size);
  }

  private _hasNextCompaniesPage(page: BdPageable<BdCompany>): boolean {
    // Stop when the backend flags the last page, when there is no further page
    // to request, or when a page came back empty — the last guard prevents an
    // endless loop if the response omits the paging metadata.
    if (page.last || page.content.length === 0) {
      return false;
    }
    return page.number + 1 < page.totalPages;
  }

  /**
   * Who can reach this shareable, and through which derivation path (BDA-5988).
   * Requires UPDATE on the shareable.
   *
   * `companyId` marks each path active or inactive in that company context;
   * note that the backend also requires membership in it and answers 403
   * otherwise, indistinguishable from the "no UPDATE" 403 — see
   * `BdPermissionsTableComponent`, which retries without it.
   */
  public accessGraph(
    type: ShareableType,
    id: string,
    options?: AccessGraphOptions,
  ): Observable<AccessGraph> {
    let params = new HttpParams();
    if (options?.companyId) {
      params = params.set('companyId', options.companyId);
    }
    if (options?.expandUsers) {
      params = params.set('expand', 'users');
    }
    return this._httpClient.get<AccessGraph>(this._store.accessGraph(type, id), {
      params,
    });
  }

  public myUsers(
    params?: BdMyUsersParams,
  ): Observable<BdPageable<BdGraphApiUser>> {
    let httpParams = new HttpParams();
    if (params?.companyId) {
      httpParams = httpParams.set('companyId', params.companyId);
    }
    if (params?.query) {
      httpParams = httpParams.set('query', params.query);
    }
    if (params?.page != null) {
      httpParams = httpParams.set('page', params.page);
    }
    if (params?.size != null) {
      httpParams = httpParams.set('size', params.size);
    }
    if (params?.sort) {
      for (const sort of params.sort) {
        httpParams = httpParams.append('sort', sort);
      }
    }
    return this._httpClient.get<BdPageable<BdGraphApiUser>>(
      this._store.users(),
      { params: httpParams },
    );
  }

  public sharePreview(
    type: ShareableType,
    id: string,
    granteeId: string,
    granteeType: ShareTargetType,
  ): Observable<SharePreview> {
    const params = new HttpParams()
      .set('granteeId', granteeId)
      .set('granteeType', granteeType);
    return this._httpClient.get<SharePreview>(this._store.sharePreview(type, id), {
      params,
    });
  }

  public findUser(
    query: string,
    companyId?: string,
  ): Observable<BdGraphApiUser | undefined> {
    let params = new HttpParams()
      .set('query', query)
      .set('size', '1')
      .set('page', '0');
    if (companyId) {
      params = params.set('companyId', companyId);
    }
    return this._httpClient
      .get<BdPageable<BdGraphApiUser>>(this._store.users(), { params })
      .pipe(map((response) => response.content[0]));
  }

  /**
   * Returns the distinct label texts the current user can see — i.e. labels
   * sitting on any shareable they have READ access to — alphabetically
   * (case-insensitively) sorted by the backend. Mirrors the resource-list
   * scoping so the picker only offers labels relevant to the current view.
   *
   * Label identity is the text (exact, case-sensitive); colors are irrelevant
   * here, so this returns plain strings to be fed verbatim into the
   * `?label=` filter of {@link myShareables}.
   */
  public allLabels(
    shareableType?: ShareableType[],
    excludeShareableType?: ShareableType[],
    companyId?: string,
  ): Observable<string[]> {
    const url = this._store.allLabels();

    let params = new HttpParams();
    if (shareableType && shareableType.length > 0) {
      params = params.appendAll({ shareableType: shareableType });
    }
    if (excludeShareableType && excludeShareableType.length > 0) {
      params = params.appendAll({ excludeShareableType: excludeShareableType });
    }
    if (companyId) {
      params = params.set('companyId', companyId);
    }

    return this._httpClient.get<string[]>(url, { params });
  }

  /**
   * Returns all labels attached to a shareable.
   *
   * Requires READ permission on the target shareable.
   */
  public labels(
    shareableType: ShareableType,
    shareableId: string,
  ): Observable<ShareableLabel[]> {
    const url = this._store.labels(shareableType, shareableId);
    return this._httpClient.get<ShareableLabel[]>(url);
  }

  /**
   * Upserts a label on a shareable — adds it or replaces the color if a
   * label with the same text already exists. Returns the full updated
   * label list.
   *
   * Requires UPDATE permission on the target shareable.
   */
  public setLabel(
    shareableType: ShareableType,
    shareableId: string,
    label: ShareableLabel,
  ): Observable<ShareableLabel[]> {
    const url = this._store.labels(shareableType, shareableId);
    return this._httpClient.put<ShareableLabel[]>(url, label);
  }

  /**
   * Removes a label from a shareable by its text.
   *
   * Requires UPDATE permission on the target shareable.
   */
  public removeLabel(
    shareableType: ShareableType,
    shareableId: string,
    text: string,
  ): Observable<void> {
    const url = this._store.labels(shareableType, shareableId);
    return this._httpClient.request<void>('DELETE', url, { body: { text } });
  }

  private _getKey(
    resourceId: string,
    shareableType: ShareableType,
    permission: BdSharePermission,
  ): string {
    return `${resourceId}_${shareableType}_${permission}`;
  }
}
